import * as React from 'react';
import {
  HealthProfilePageWrapper,
  MoreOptionsCard,
  MoreOptionsLink,
} from '@leagueplatform/health-profile-common';
import {
  Caption,
  Flex,
  HeadingOne,
  SecondaryButton,
  SubtitleOne,
} from '@leagueplatform/genesis-commons';
import { useIntl } from '@leagueplatform/locales';
import {
  LoadingIndicator,
  Sidebar,
} from '@leagueplatform/web-common-components';
import {
  DATE_FORMAT,
  useFormatDateByUserProfileLocation,
} from '@leagueplatform/web-common';
import { MetricSource, MetricType } from '@leagueplatform/dashboard-api';
import {
  useDataTypeConfigMap,
  isSupportedUnitMetricType,
} from '#constants/use-data-type-config.ts';
import { useLocaleUnitFormatter } from '#hooks/use-locale-unit-formatter.ts';
import { useInfiniteHealthMetrics } from '#hooks/use-get-health-metrics.hook.ts';
import { useLocaleMetricValueFormatter } from '#hooks/use-metric-value-formatter.ts';
import { ListItem } from '#components/list-item/index.ts';
import { ErrorPanel } from '#components/error-panel/index.ts';
import { useHealthMetricDocumentTitle } from '#hooks/use-health-metric-document-title.ts';
import { useParamsLegacy as useParams } from '@leagueplatform/routing';
import { track } from '@leagueplatform/analytics';

type Params = {
  dataType: MetricType;
};

export const AllRecordedData = () => {
  const { dataType } = useParams<Params>();
  const { formatMessage, $tmap } = useIntl();
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();
  const { formatUnit } = useLocaleUnitFormatter();
  const { formatMetricValue } = useLocaleMetricValueFormatter();
  const metric = useDataTypeConfigMap().metric(dataType);

  React.useEffect(() => {
    track('dashboard.screen_loaded.all_recorded_data_metric_type', {
      metric_type: dataType,
      metric_id: '',
      metric_status: '',
    });
  }, [dataType]);

  const {
    isFetching: isLoadingHealthMetrics,
    isError: isErrorHealthMetrics,
    data: healthMetrics,
    isRefetching: isRefetchingHealthMetrics,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    error,
  } = useInfiniteHealthMetrics({
    filter: {
      type: dataType,
    },
    fields: { type: ['type', 'value', 'source', 'unit', 'timestamp'] },
  });

  const getMetricSourceMessage = $tmap<MetricSource>({
    apple_health: 'APPLE_HEALTH',
    bluetooth: 'BLUETOOTH',
    fitbit: 'FITBIT',
    garmin: 'GARMIN',
    google_fit_sdk: 'GOOGLE_FIT_SDK',
    ihealth: 'IHEALTH',
    omron: 'OMRON',
    polar: 'POLAR',
    samsung_health: 'SAMSUNG_HEALTH',
    strava: 'STRAVA',
    vital_snap: 'VITAL_SNAP',
    withings: 'WITHINGS',
    health_plan: 'HEALTH_PLAN',
    no_source_data: 'NO_SOURCE_DATA',
    unavailable: 'UNAVAILABLE',
    backfilled: 'BACKFILLED',
    android_health_connect: 'ANDROID_HEALTH_CONNECT',
  });

  useHealthMetricDocumentTitle(
    formatMessage({
      id: 'ALL_RECORDED_DATA',
    }),
    metric,
  );

  if (isLoadingHealthMetrics && !healthMetrics) {
    return <LoadingIndicator />;
  }

  // Sanity check if for whatever reason an unsupported data type is passed in during runtime we ignore it.
  if (!isSupportedUnitMetricType(dataType)) {
    return null;
  }

  return (
    <HealthProfilePageWrapper
      title={formatMessage({
        id: 'ALL_RECORDED_DATA',
      })}
      sidebar={
        <Sidebar>
          <MoreOptionsCard>
            <MoreOptionsLink
              to="data-sources"
              onClick={() =>
                track(
                  'dashboard.button_clicked.metric_item_metric_type.navigate_to_metric_tab',
                  {
                    metric_type: dataType,
                    metric_id: '',
                    metric_status: '',
                    content_id: '',
                    contentful_system: '',
                    detail: 'data sources',
                  },
                )
              }
            >
              {formatMessage({ id: 'VIEW_DATA_SOURCES' })}
            </MoreOptionsLink>
          </MoreOptionsCard>
        </Sidebar>
      }
    >
      <HeadingOne as="h2" paddingBottom="twoAndHalf" paddingTop="oneAndHalf">
        {metric}
      </HeadingOne>
      {isErrorHealthMetrics ? (
        <ErrorPanel
          error={error}
          isRefetching={isRefetchingHealthMetrics}
          onRetry={refetch}
        />
      ) : (
        <>
          {healthMetrics?.pages.map((page) =>
            page.data.map((dataMetric) => {
              const { unit, value, source, timestamp, type } = dataMetric;
              const date = formatDateWithUserProfileLocation(
                timestamp,
                DATE_FORMAT,
              );

              if (value === null) {
                return null;
              }

              return (
                <ListItem key={timestamp}>
                  <Flex flexDirection="column">
                    <SubtitleOne>
                      {formatMetricValue(value, type)}{' '}
                      {formatUnit(type, unit, value)}
                    </SubtitleOne>
                    <Caption color="onSurface.text.subdued">
                      {getMetricSourceMessage(source)}
                    </Caption>
                  </Flex>
                  <SubtitleOne
                    paddingTop="half"
                    paddingBottom="half"
                    color="onSurface.text.subdued"
                    fontWeight="regular"
                  >
                    {date}
                  </SubtitleOne>
                </ListItem>
              );
            }),
          )}
          <Flex justifyContent="center" mt="twoAndHalf">
            <SecondaryButton
              disabled={isFetchingNextPage}
              onClick={() => {
                fetchNextPage();
              }}
            >
              {formatMessage({ id: 'LOAD_MORE' }).toLocaleUpperCase()}
            </SecondaryButton>
          </Flex>
        </>
      )}
    </HealthProfilePageWrapper>
  );
};
